import { useNavigate } from 'react-router-dom'
import { sessionApi } from '@/lib/api/sessions'
import type { ChatSession } from '@/types/chat'
import { Button } from '@/components/ui/button'

interface SessionRowActionsProps {
  session: ChatSession
  busy: boolean
  onExport: () => void
  onReload: () => void
  onBusyChange: (busy: boolean) => void
}

export function SessionRowActions({ session, busy, onExport, onReload, onBusyChange }: SessionRowActionsProps) {
  const navigate = useNavigate()

  const run = async (action: () => Promise<unknown>) => {
    onBusyChange(true)
    try {
      await action()
      onReload()
    } finally {
      onBusyChange(false)
    }
  }

  const actions: Array<{ id: string; label: string; show: boolean; run: () => Promise<unknown> }> = [
    {
      id: 'open',
      label: 'Open',
      show: session.status === 'closed' || session.status === 'archived',
      run: async () => {
        await sessionApi.reopenSession(session.id)
        navigate('/chat', { state: { openSessionId: session.id } })
      },
    },
    {
      id: 'duplicate',
      label: 'Duplicate',
      show: true,
      run: async () => {
        const duplicated = await sessionApi.duplicateSession(session.id)
        navigate('/chat', { state: { openSessionId: duplicated.id } })
      },
    },
    {
      id: 'close',
      label: 'Close',
      show: session.status === 'open',
      run: () => sessionApi.closeSession(session.id),
    },
    {
      id: 'archive',
      label: 'Archive',
      show: session.status === 'open' || session.status === 'closed',
      run: () => sessionApi.archiveSession(session.id),
    },
    {
      id: 'restore',
      label: 'Restore',
      show: session.status === 'archived',
      run: () => sessionApi.restoreSession(session.id),
    },
  ]

  const visibleActions = actions.filter((action) => action.show)

  return (
    <>
      <div className="hidden flex-wrap items-center gap-2 md:flex">
        {visibleActions.map((action) => (
          <Button
            key={action.id}
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => void run(action.run)}
            data-testid={`session-${action.id}-${session.id}`}
          >
            {action.label}
          </Button>
        ))}
        <Button
          size="sm"
          variant="outline"
          onClick={onExport}
          data-testid={`session-export-${session.id}`}
        >
          Export
        </Button>
      </div>

      <div className="flex flex-col gap-2 md:hidden" data-testid={`session-actions-mobile-${session.id}`}>
        <select
          className="min-h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          defaultValue=""
          disabled={busy}
          onChange={(event) => {
            const value = event.target.value
            event.target.value = ''
            if (!value) return

            if (value === 'export') {
              onExport()
              return
            }

            const action = visibleActions.find((item) => item.id === value)
            if (action) void run(action.run)
          }}
          data-testid={`session-actions-menu-${session.id}`}
          aria-label={`Actions for ${session.title}`}
        >
          <option value="">Actions…</option>
          {visibleActions.map((action) => (
            <option key={action.id} value={action.id}>
              {action.label}
            </option>
          ))}
          <option value="export">Export</option>
        </select>
      </div>
    </>
  )
}
